import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { withAuthenticationRequired } from '@auth0/auth0-react';
// API
import { useAuth } from '../../hooks/useAuth';
import { useEnv } from '../../context/env-context';
import { makeRequest } from '../../utils/makeRequest';
import { toast } from 'react-toastify';
// components
import Loading from '../../components/Loading';
import { Spinner } from '../../components/Spinner';
import NewFundingLayout from './components/NewFundingLayout';

// TODO: rename new funding page to funding
function NewFunding() {
  const routeParams = useParams();
  const fundingId = routeParams?.id;
  const navigate = useNavigate();

  const { getAccessTokenSilently } = useAuth();
  const { apiOriginConsumer, apiOriginFunding } = useEnv();

  const [funding, setFunding] = useState(null);
  const [consumer, setConsumer] = useState(null);
  const [bridgePointsBalance, setBridgePointsBalance] = useState(null);
  const [totalExpectedBalance, setTotalExpectedBalance] = useState(null);

  const getRequest = useCallback(
    async ({ url, params, onError, onSuccess }) => {
      const token = await getAccessTokenSilently();

      if (!token) {
        return null;
      }

      const config = { token, url, method: 'GET', params };

      try {
        const response = await makeRequest(config);

        onSuccess(response);

        return response;
      } catch (error) {
        onError(error);

        return null;
      }
    },
    [getAccessTokenSilently],
  );

  const getFunding = useCallback(
    () =>
      getRequest({
        url: `${apiOriginFunding}/v2/get/${fundingId}`,
        onSuccess: setFunding,
        onError: error => {
          if (error.message === `Funding ${fundingId} not found!`) {
            navigate('/fundings');
            toast.error(`Advance ID ${fundingId} not found!`);
          } else {
            toast.error(error.message);
          }
        },
      }),
    [apiOriginFunding, fundingId, getRequest, navigate],
  );

  const getConsumer = useCallback(
    consumerId =>
      getRequest({
        url: `${apiOriginConsumer}/get/${consumerId}`,
        onSuccess: setConsumer,
        onError: error => toast.error(error.message),
      }),
    [apiOriginConsumer, getRequest],
  );

  const getBridgePointsBalance = useCallback(
    consumerId =>
      getRequest({
        url: `${apiOriginConsumer}/bridgepoints/balance`,
        params: { consumerId },
        onSuccess: setBridgePointsBalance,
        onError: error => toast.error(error.message),
      }),
    [apiOriginConsumer, getRequest],
  );

  const refreshFundingData = useCallback(async () => {
    const response = await getFunding();

    if (response?.consumerId) {
      getConsumer(response.consumerId);
      getBridgePointsBalance(response.consumerId);
    }
  }, [getFunding, getConsumer, getBridgePointsBalance]);

  useEffect(() => {
    refreshFundingData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!funding || !consumer) {
    return (
      <div className="mt-5">
        <Spinner visible />
      </div>
    );
  }

  return (
    <NewFundingLayout
      funding={funding}
      consumer={consumer}
      bridgePointsBalance={bridgePointsBalance}
      totalExpectedBalance={totalExpectedBalance}
      setTotalExpectedBalance={setTotalExpectedBalance}
      refreshFundingData={refreshFundingData}
    />
  );
}

export default withAuthenticationRequired(NewFunding, { onRedirecting: () => <Loading /> });
